import { motion } from "framer-motion";
import { HeartHandshake, FileSearch, HandMetal, ArrowRight } from "lucide-react";
import { scrollToId } from "@/pages/LandingPage";

const EASE = [0.22, 1, 0.36, 1];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.14, duration: 0.8, ease: EASE } }),
};

const PROMISES = [
  { icon: HeartHandshake, text: "You always talk to me directly — never a call center, never a bot." },
  { icon: FileSearch, text: "You see the official plan documents and pricing before you commit to anything." },
  { icon: HandMetal, text: "No pressure, ever. If it's not right for you, I'll tell you that too." },
];

export default function AboutJohn() {
  return (
    <section id="about-john" className="bg-white py-24 md:py-36 scroll-mt-20" data-testid="about-john-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <motion.div
          variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={0}
          className="lg:col-span-5 relative max-w-md mx-auto lg:mx-0 w-full"
        >
          <div className="absolute -inset-5 bg-blue-600/15 blur-3xl rounded-[2.5rem]" />
          <div className="relative rounded-[2rem] overflow-hidden border border-slate-200 shadow-2xl shadow-blue-950/15 bg-gradient-to-b from-[#EFF6FF] to-white">
            <img
              src="/john-jones.png"
              alt="John Jones, independent LegalShield associate"
              className="w-full h-[440px] object-cover object-top"
              data-testid="about-john-photo"
            />
          </div>
          <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-[#0B192C] text-white rounded-full px-6 py-2.5 shadow-lg whitespace-nowrap">
            <span className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-sky-300">Your associate, start to finish</span>
          </div>
        </motion.div>

        <div className="lg:col-span-7">
          <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={0}
            className="font-mono-accent text-xs uppercase tracking-[0.28em] text-blue-600 mb-6">
            The person behind the page
          </motion.p>
          <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={1}
            className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B192C] leading-[1.1] mb-8">
            Hi, I'm John.
          </motion.h2>
          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={2}
            className="space-y-5 text-lg text-slate-600 leading-relaxed max-w-2xl mb-10">
            <p>
              I'm an independent associate, and I got into this after watching people I care about
              put off legal help they genuinely needed — not because the problem was small, but
              because the hourly bill felt bigger.
            </p>
            <p>
              When I found out a flat monthly membership could put a law firm in someone's corner
              for roughly the cost of a couple of pizzas, sharing it stopped feeling like selling.
              It felt like the obvious thing to do.
            </p>
          </motion.div>

          <div className="space-y-4 mb-10">
            {PROMISES.map((p, i) => (
              <motion.div key={p.text} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i + 2}
                className="flex items-start gap-4" data-testid={`about-john-promise-${i}`}>
                <span className="w-10 h-10 rounded-xl bg-[#EFF6FF] border border-blue-200 text-blue-700 flex items-center justify-center shrink-0">
                  <p.icon size={18} strokeWidth={1.9} />
                </span>
                <p className="text-slate-700 leading-relaxed pt-2">{p.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={3}>
            <button
              data-testid="about-john-cta"
              onClick={() => scrollToId("#get-started")}
              className="group flex items-center gap-2 bg-[#1D4ED8] hover:bg-blue-800 text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-300"
            >
              Say hello — I reply myself
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
